import { z } from "zod";
import { channelWidgetSpecSchema } from "./channel-widget-artifacts.js";
/*
 * One channel's widgets applied to others (docs/audits/widget-reviews-verified-2026-10-10.md, item 27): a
 * team that keeps a channel for each project wants the same widgets on every one, each over that project's
 * own records. One channel is the pattern: the widgets on its Overview. Applying it gives each target those
 * widgets over the target's own data set of the same name, and remembers which widget each was first copied
 * from, so applying it again, from that channel or from any copy of it, changes only what differs and never
 * makes a widget twice.
 *
 * What a widget measures follows the pattern. What a channel is aiming at does not: a target, a date and a
 * reference line are each channel's own, so a new widget starts without them and says what it needs, and
 * one a channel has already set is kept when the pattern is applied again. Written notes are one channel's
 * too, and are never carried to another.
 */
/** How many channels one application reaches: each is a change of its own, published whole or not at all. */
export const CHANNEL_WIDGET_SET_TARGETS_MAX = 10;
/** How long what is said of one channel may be: a reason is cut to this, never refused for its length. */
export const CHANNEL_WIDGET_SET_NOTE_MAX = 1_500;
const id = z.string().trim().min(1).max(200);
export const channelWidgetSetApplyInputSchema = z.object({
    idempotencyKey: z.string().trim().min(8).max(200),
    /** The channels the pattern is applied to, each named once. */
    targetChannelIds: z.array(id).min(1).max(CHANNEL_WIDGET_SET_TARGETS_MAX)
        .refine((ids) => new Set(ids).size === ids.length, "Each channel is named once."),
}).strict();
/** What became of the pattern in one channel. */
export const channelWidgetSetChannelSchema = z.object({
    channelId: id,
    /** applied: widgets were made or changed, as one change. unchanged: it already matched. refused: nothing was changed there. */
    outcome: z.enum(["applied", "unchanged", "refused"]),
    /** The widgets made, each placed on the channel's Overview. */
    created: z.number().int().nonnegative(),
    revised: z.number().int().nonnegative(),
    /** Why nothing was changed there, or what a person should know about how it was applied, in words they can act on. */
    note: z.string().min(1).max(CHANNEL_WIDGET_SET_NOTE_MAX).optional(),
    /** The change that was published there, which undoing takes back as one step. */
    changesetId: z.string().min(1).optional(),
}).strict();
export const channelWidgetSetApplyResultSchema = z.object({
    sourceChannelId: id,
    /** How many widgets the pattern carries. */
    widgets: z.number().int().nonnegative(),
    /** The names of the pattern's notes, which are written for one channel and are not carried. */
    leftOut: z.array(z.string().min(1).max(200)).max(40),
    channels: z.array(channelWidgetSetChannelSchema).max(CHANNEL_WIDGET_SET_TARGETS_MAX),
}).strict();
/** Whether a set carries a widget: everything computed from a channel's own records, and no written notes. */
export const inWidgetSet = (spec) => spec.widget.type !== "insight_list" && spec.widget.type !== "risk_list";
/**
 * A goal's own target: a channel keeps the targets it set when the pattern's goals are applied again. A goal
 * has no id, so it is known by its label, each of the channel's goals standing for one of the pattern's; a
 * goal the pattern has renamed is the one in its place, so a new name does not cost a channel its target.
 */
function goalsWith(goals, own) {
    if (!Array.isArray(goals))
        return goals;
    const kept = (Array.isArray(own) ? own : []);
    const taken = new Set();
    const named = goals.map((goal) => {
        const at = kept.findIndex((held, index) => !taken.has(index) && held.label === goal.label);
        if (at >= 0)
            taken.add(at);
        return at;
    });
    return goals.map((goal, index) => {
        const at = named[index] >= 0 ? named[index] : index < kept.length && !taken.has(index) ? index : -1;
        return { ...goal, target: at >= 0 ? kept[at].target ?? null : null };
    });
}
/**
 * The spec a pattern's widget takes in another channel: what it measures, over that channel's own data
 * set. Its inputs are the channel's: where the channel already has this widget (`own`) they are kept as
 * the channel set them, and otherwise they start empty, so a progress bar asks for its target and a chart
 * has no reference line until someone gives that channel one.
 */
export function setWidgetSpec(pattern, objectTypeId, own) {
    const from = pattern.widget;
    const kept = own && own.widget.type === pattern.widget.type ? own.widget : null;
    const widget = { ...from };
    // A dashboard chart's reference line and a progress's target are both "target"; a countdown has its day and the key date after it.
    for (const input of ["target", "date", "next"]) {
        if (!(input in from) && !(kept && input in kept))
            continue;
        if (kept && kept[input] !== undefined)
            widget[input] = kept[input];
        else if (input === "target" && from.type === "progress")
            widget[input] = null;
        else if (input === "date")
            widget[input] = null;
        else
            delete widget[input];
    }
    if (from.type === "goal_rings")
        widget.goals = goalsWith(from.goals, kept?.goals);
    return channelWidgetSpecSchema.parse({ objectTypeId, widget });
}
const ordered = (value) => {
    if (Array.isArray(value))
        return value.map(ordered);
    if (value === null || typeof value !== "object")
        return value;
    return Object.fromEntries(Object.entries(value).filter(([, held]) => held !== undefined).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([key, held]) => [key, ordered(held)]));
};
/** Whether two values say the same thing, whatever order their keys were written in. */
export const sameSettings = (left, right) => JSON.stringify(ordered(left)) === JSON.stringify(ordered(right));
/** Whether two specs say the same thing. Their own id and position are not read: a widget's id is its artifact's, and where it sits is its placement's. */
export function sameWidgetSpec(left, right) {
    const said = (spec) => {
        const { id: _id, position: _position, ...widget } = spec.widget;
        return { objectTypeId: spec.objectTypeId, widget };
    };
    return sameSettings(said(left), said(right));
}
